import type { AssetDescription } from '@play-co/timestep-core';
import {
  ImageScaleView,
  MovieClip,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import { spineLoader } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import SpineAnimationView from 'src/game/components/shared/SpineAnimationView';
import { dreamDriftWearPet } from 'src/game/dreamDrift/dreamDrift.costumes';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import sounds from 'src/lib/sounds';
import type { BlockId } from '../../minigame/match2-timestep/game/defs/block';
import { config } from '../../minigame/match2-timestep/game/defs/config';
import type { BasicBlockEntity } from '../../minigame/match2-timestep/game/entities/BlockEntity';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { getMatch2AssetPath } from '../../minigame/match2-timestep/game/util/assets';
import { mapToViewPosition } from '../../minigame/match2-timestep/game/util/mapTools';

/** Match-2 MovieClip used by temporary Water Balloon flight views. */
const SKY_SUMMONER_BOMB_ANIMATION_ASSET = {
  loadMethod: MovieClip.animationLoader,
  url: getMatch2AssetPath('blocks/power/bomb'),
};

/** Match-2 MovieClip used by temporary Firecracker flight views. */
const SKY_SUMMONER_ROCKET_ANIMATION_ASSET = {
  loadMethod: MovieClip.animationLoader,
  url: getMatch2AssetPath('blocks/power/rocket'),
};

/** Existing board images used by color-matched Lollipop flight views. */
const SKY_SUMMONER_LOLLIPOP_IMAGES: Partial<Record<BlockId, string>> = {
  cubeBlue: getMatch2AssetPath('power/block.cube.blue.png'),
  cubeGreen: getMatch2AssetPath('power/block.cube.green.png'),
  cubePurple: getMatch2AssetPath('power/block.cube.purple.png'),
  cubeRed: getMatch2AssetPath('power/block.cube.red.png'),
  cubeYellow: getMatch2AssetPath('power/block.cube.yellow.png'),
};

/** Spine export used by the Sky Summoner opening presentation. */
export const skySummonerPowerAnimationAsset = {
  loadMethod: spineLoader,
  url: 'assets/ui/dreamDrift/powerSummoner/spine.Dreamdrift.wormhole',
};

/** All assets required by the Sky Summoner opening presentation. */
export const skySummonerPowerAnimationAssets: (
  | string
  | AssetDescription<unknown>
)[] = [
  skySummonerPowerAnimationAsset,
  SKY_SUMMONER_BOMB_ANIMATION_ASSET,
  SKY_SUMMONER_ROCKET_ANIMATION_ASSET,
  ...Object.values(SKY_SUMMONER_LOLLIPOP_IMAGES),
];

/** Scale authored on the Sky Summoner pet placeholder in the setup pose. */
const SKY_SUMMONER_PET_SETUP_SCALE = 0.5;

/** Animate one temporary power block from the wormhole to its target cell. */
export type SkySummonerPowerFlight = (
  target: BasicBlockEntity,
  powerBlockId: BlockId,
) => Promise<void>;

/** Apply the soft scene shake synchronized with one real board spawn. */
export type SkySummonerPowerSpawnShake = () => Promise<void>;

/** Callback that places the configured boosters during the summoning pose. */
export type SkySummonerPowerPlacement = (
  animatePowerBlock: SkySummonerPowerFlight,
  shakePowerSpawn: SkySummonerPowerSpawnShake,
) => Promise<void>;

/**
 * Apply a soft shake while one summoned power block materializes on the board.
 *
 * @param sceneRoot Root view containing the puzzle and power presentation.
 * @returns Nothing after the scene settles at its original position.
 */
async function shakeSkySummonerScene(sceneRoot: View): Promise<void> {
  // Preserve the responsive scene position so every launch settles cleanly.
  const originX = sceneRoot.style.x ?? 0;
  const originY = sceneRoot.style.y ?? 0;

  // Mirror the subtle three-beat shake used by Whirlwind Dancer movement.
  await animate(sceneRoot)
    .now({ x: originX - 3, y: originY + 2 }, 50, animate.linear)
    .then({ x: originX + 3, y: originY - 2 }, 50, animate.linear)
    .then({ x: originX, y: originY }, 50, animate.easeOut)
    .toPromise();
}

/**
 * Play the supplied Sky Summoner wormhole and pet animations over the puzzle.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param onSummon Callback placing the configured boosters during the idle phase.
 * @returns Nothing after both Spine layers and the board placement complete.
 */
export async function runSkySummonerPowerAnimation(
  game: GameScene,
  onSummon: SkySummonerPowerPlacement,
): Promise<void> {
  // Load the shared export before changing the visible puzzle scene.
  await spineLoader(skySummonerPowerAnimationAsset.url);

  // Resolve the standard Match-2 scene hierarchy from the game stage.
  const puzzleContainer = game.stage.view.getSuperview();
  const sceneRoot = puzzleContainer?.getSuperview();
  if (!puzzleContainer || !sceneRoot) return;

  // Keep the black dim behind the puzzle grid while covering the scene beneath it.
  const dim = new View({
    superview: sceneRoot,
    width: sceneRoot.style.width,
    height: sceneRoot.style.height,
    backgroundColor: '#000000',
    opacity: 0,
    canHandleEvents: false,
    zIndex: (puzzleContainer.style.zIndex ?? 0) - 0.5,
  });

  // Center the native 1080x1920 Spine composition over the responsive scene.
  const animationContainer = new View({
    superview: sceneRoot,
    x: sceneRoot.style.width / 2,
    y: sceneRoot.style.height / 2,
    scale: DOWNSCALE_FACTOR,
    canHandleEvents: false,
    zIndex: 100000,
  });

  // Run the complete authored wormhole on its own layer to preserve rotation.
  const wormholeSpine = new SpineAnimationView({
    superview: animationContainer,
    spinePath: skySummonerPowerAnimationAsset.url,
    x: 0,
    y: -270,
    scale: 0.7,
    zIndex: 0,
    visible: false,
  });

  // Drive the pet placeholder independently from the same Spine source.
  const petSpine = new SpineAnimationView({
    superview: animationContainer,
    spinePath: skySummonerPowerAnimationAsset.url,
    x: 0,
    y: 0,
    zIndex: 1,
    visible: false,
  });

  // Instantiate the player's happy pet and dress it as Sky Summoner.
  const species = getPet().species ?? 'dog';
  const pet = createUIPet('happy', species);
  dreamDriftWearPet(pet, 'skySummoner');
  pet.updateOpts({
    superview: animationContainer,
    anchorY: 300,
    offsetY: -300,
    scale: 2.4,
    zIndex: 2,
  });

  /** Keep the dressed pet below the authored placeholder animation. */
  const updatePetWithSpine = (): void => {
    // Follow the bone backing the authored placeholder_pet slot.
    const bone = petSpine.skeleton.findBone('placeholder_bear');
    if (!bone) return;

    // Follow the placeholder transform with the requested downward screen offset.
    pet.updateOpts({
      x: bone.worldX,
      y: bone.worldY + 50,
      r: (bone.getWorldRotationX() * Math.PI) / 180,
      scaleX: bone.getWorldScaleX() * SKY_SUMMONER_PET_SETUP_SCALE,
      scaleY: bone.getWorldScaleY() * SKY_SUMMONER_PET_SETUP_SCALE,
    });
  };
  animationContainer.setTick(updatePetWithSpine);

  /**
   * Fly one temporary power-block view from the wormhole to its board cell.
   *
   * @param target Basic block that will be replaced after the flight.
   * @param powerBlockId Power-block appearance represented by the temporary view.
   * @returns Nothing after the temporary view reaches its target and is removed.
   */
  const animatePowerBlock = async (
    target: BasicBlockEntity,
    powerBlockId: BlockId,
  ): Promise<void> => {
    // Resolve the live wormhole center from the authored effects layer.
    const wormholeBone = wormholeSpine.skeleton.findBone('wormhole');
    if (!wormholeBone) return;

    // Convert the Spine and board positions into the shared scene-root space.
    const wormholePosition = wormholeSpine.getPosition(sceneRoot);
    const stagePosition = game.stage.view.getPosition(sceneRoot);
    const mapPosition = mapToViewPosition(target.c.position.mapPosition, true);
    const fromX =
      wormholePosition.x + wormholeBone.worldX * wormholePosition.scale;
    const fromY =
      wormholePosition.y + wormholeBone.worldY * wormholePosition.scale;
    const toX = stagePosition.x + mapPosition.x * stagePosition.scale;
    const toY = stagePosition.y + mapPosition.y * stagePosition.scale;
    const midpointX = (fromX + toX) / 2;
    const midpointY = (fromY + toY) / 2;

    // Match the temporary view to the real board block's appearance.
    const isBomb = powerBlockId === 'bomb';
    const targetScale = (config.tile.size / 88) * stagePosition.scale;
    const flightOptions = {
      superview: sceneRoot,
      x: fromX,
      y: fromY,
      scale: targetScale,
      r: powerBlockId === 'rocketHorizontal' ? Math.PI / 2 : 0,
      centerOnOrigin: true,
      canHandleEvents: false,
      zIndex: 100001,
    };
    const lollipopImage = SKY_SUMMONER_LOLLIPOP_IMAGES[powerBlockId];
    const powerBlock = lollipopImage
      ? new ImageScaleView({
          ...flightOptions,
          image: lollipopImage,
          width: 88,
          height: 88,
          scaleMethod: 'contain',
        })
      : new MovieClip({
          ...flightOptions,
          url: isBomb
            ? SKY_SUMMONER_BOMB_ANIMATION_ASSET.url
            : SKY_SUMMONER_ROCKET_ANIMATION_ASSET.url,
          defaultAnimation: isBomb ? 'waterballoon_idle' : 'rocket_idle',
          fps: 40,
        });

    // Grow to twice board size by mid-flight, then settle at board size on arrival.
    await animate(powerBlock)
      .now(
        { x: midpointX, y: midpointY, scale: targetScale * 2 },
        200,
        animate.easeIn,
      )
      .then({ x: toX, y: toY, scale: targetScale }, 200, animate.easeOut)
      .toPromise();

    // Remove the temporary view before the real board block is spawned.
    powerBlock.removeFromSuperview();
  };

  /** Apply the presentation shake when one real power block starts spawning. */
  const shakePowerSpawn = (): Promise<void> => {
    // Shake the shared scene root while the Match-2 block materializes.
    return shakeSkySummonerScene(sceneRoot);
  };

  // Dim the scene before revealing either Spine layer.
  await animate(dim).then({ opacity: 0.5 }, 300, animate.linear).toPromise();

  // Start the complete wormhole and pet entrance at the same authored origin.
  wormholeSpine.show();
  sounds.sfx.play('dreamDriftPortal');
  petSpine.show();
  const wormholeComplete = wormholeSpine.start({
    id: 'wormhole',
    mix: 0,
  }).complete;
  await petSpine.start({ id: 'pet_init', mix: 0 }).complete;

  /** Play the two authored pet idle passes as one continuous sequence. */
  const playIdleSequence = async (): Promise<void> => {
    await petSpine.start({ id: 'pet_idle', mix: 0 }).complete;
    await petSpine.start({ id: 'pet_idle', mix: 0 }).complete;
  };

  // Start placement after the first idle pass begins, then keep both continuous.
  const idleSequence = playIdleSequence();
  const placementComplete = onSummon(animatePowerBlock, shakePowerSpawn);
  await Promise.all([idleSequence, placementComplete]);

  // Animate the temporary hero pet away before revealing the scene pet.
  await petSpine.start({ id: 'pet_exit', mix: 0 }).complete;

  // Keep the opener pending until the wormhole's authored exit also completes.
  await wormholeComplete;

  // Restore the scene behind the still-bright puzzle grid.
  await animate(dim).then({ opacity: 0 }, 300, animate.linear).toPromise();

  // Remove all temporary animation state after the presentation finishes.
  animationContainer.setTick(null);
  wormholeSpine.state.clearTracks();
  wormholeSpine.state.clearListeners();
  petSpine.state.clearTracks();
  petSpine.state.clearListeners();
  animationContainer.removeFromSuperview();
  dim.removeFromSuperview();
}
